import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  Divider,
  Theme,
} from "@mui/material";
import { motion } from "framer-motion";
import React from "react";
import ContentSection from "../ContentSection";
import { useTranslation } from "react-i18next";

interface YouthContentProps {
  theme: Theme;
  getBannerImage: () => string;
}

const YouthContent: React.FC<YouthContentProps> = ({
  theme,
  getBannerImage,
}) => {
  const { t } = useTranslation();

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
        maxWidth: "100%",
        mx: "auto",
      }}
    >
      {/* Main white card containing all content */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "white",
          maxWidth: "100%",
          mx: "auto",
        }}
      >
        {/* Image section */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 250, sm: 350, md: 400, lg: 450, xl: 500 },
          }}
        >
          <img
            src={getBannerImage()}
            alt="Youth Portal"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
              p: { xs: 3, sm: 4, md: 5 },
            }}
          >
            <Typography
              variant="h3"
              fontWeight={700}
              color="white"
              gutterBottom
              sx={{ fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" } }}
            >
              Welcome to CYPMS
            </Typography>
            <Typography
              variant="h5"
              fontWeight={500}
              color="white"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" } }}
            >
              Your Gateway to Growth
            </Typography>
          </Box>
        </Box>

        {/* Content sections */}
        <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <YouthIntroSection />
          <Divider sx={{ my: 4 }} />
          <YouthFeaturesSection theme={theme} />
          <Divider sx={{ my: 4 }} />
          <YouthAfterRegistrationSection theme={theme} />
        </Box>
      </Card>
    </Container>
  );
};

// Intro section component
const YouthIntroSection: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7 }}
    >
      <Typography
        variant="h4"
        fontWeight={700}
        gutterBottom
        color="primary"
        sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" } }}
      >
        What Is CYPMS?
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
        The CODICE Youth Program Management System (CYPMS) is a smart, secure,
        and user-friendly platform designed to support your journey through DC
        youth employment and development programs—like MBSYEP and more.
      </Typography>
      <Typography variant="body1" sx={{ mb: 4 }} color="text.secondary">
        Whether you're looking to apply for jobs, track your work hours, or get
        paid—CYPMS has everything you need in one place.
      </Typography>
    </motion.div>
  );
};

interface SectionWithThemeProps {
  theme: Theme;
}

// Features section component
const YouthFeaturesSection: React.FC<SectionWithThemeProps> = ({ theme }) => {
  return (
    <>
      <Typography
        variant="h4"
        fontWeight={700}
        gutterBottom
        color="primary"
        sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" } }}
      >
        Things You Should Know About CYPMS
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
        Here's what you can do once you've registered:
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Enroll in Programs"
            content="Apply to youth employment or internship programs directly through the portal."
            delay={0.1}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Explore & Apply for Jobs"
            content="View open job opportunities, apply with ease, and track your application status."
            delay={0.2}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Submit Timesheets"
            content="Log your work hours daily and view your timesheet history in real time."
            delay={0.3}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Track Your Pay"
            content="See how much you've earned, download pay stubs, and view past salary info."
            delay={0.4}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Complete Required Forms"
            content="Fill out and submit your W-4, DC D-4, consent forms, and more—all online."
            delay={0.5}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Use AI-Powered Assistance"
            content="Need help? Our built-in virtual assistant can guide you 24/7 on navigating the platform, writing resumes, or finding matching jobs."
            delay={0.6}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Complete Orientation & Certification"
            content="Prepare for your program experience with onboarding tools, quizzes, and learning materials—all within CYPMS."
            delay={0.7}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Stay Informed"
            content="Receive real-time updates, announcements, and alerts from DOES or your host site via the built-in messaging center."
            delay={0.8}
          />
        </Grid>
      </Grid>
    </>
  );
};

// After registration section component
const YouthAfterRegistrationSection: React.FC<SectionWithThemeProps> = ({
  theme,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.9 }}
    >
      <Card
        elevation={1}
        sx={{
          p: 4,
          borderRadius: 2,
          bgcolor: theme.palette.grey[50],
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
          What Happens After Registration?
        </Typography>
        <Box component="ul" sx={{ pl: 4, mb: 0 }}>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Complete Your Profile – Add your info, education, and interests
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Submit Required Documents – Parent consent, proof of residency, tax
            forms
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Take Orientation & Certification Quizzes
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Browse & Apply for Jobs
          </Box>
          <Box component="li" sx={{ mb: 0, color: "text.secondary" }}>
            Start Working & Logging Hours
          </Box>
        </Box>
      </Card>
    </motion.div>
  );
};

export default YouthContent;
